<script setup lang="ts">
import EstateListingHeader from './header/EstateListingHeader.vue'
import EstateListingSort from './sort/EstateListingSort.vue'
import EstateListingMain from './main/EstateListingMain.vue'
import EstateListingServices from './services/EstateListingServices.vue'
import EstateListingFaq from './EstateListingFaq.vue'
import WarehouseListingFilter from './filter/WarehouseListingFilter.vue'
import OfficeListingFilter from './filter/OfficeListingFilter.vue'
import RetailListingFilter from './filter/RetailListingFilter.vue'
import EstateFilterBase from './filter/EstateFilterBase.vue'
import TailoredOfferBanner from '~/components/global/TailoredOfferBanner.vue'
import type { EstateListingResponse } from '~/types/api/EstateListing'
import type { EstateType } from '~/types/EstateType'
import ShowMore from '~/components/showMore/index.vue'
import { useEstateListingData } from '~/composables/api/useEstateListingData'
import { usePaginationState } from '~/composables/pagination/usePaginationState'
import { useFilterState } from '~/composables/filter/useFilterState'
import Error from '~/components/global/Error.vue'
import Container from '~/components/layout/Container.vue'

const props = defineProps<{
  estateType: EstateType
  city?: string
}>()

const { locale } = useI18n()
const route = useRoute()

const currentCity = computed(() => {
  if (route.params?.city) {
    return capitalize(route.params.city as string)
  }
})

const listingScrollHook = ref<HTMLDivElement | null>(null)
const paginationState = usePaginationState({
  type: 'estate',
  onPageChange: () => {
    if (!listingScrollHook.value) return

    scrollToElement({
      element: listingScrollHook.value,
      ignoreHeaderHeight: true,
    })
  },
})

const filterState = useFilterState({
  paginationState,
})

let controller: AbortController
const officesResponse = useAsyncData<EstateListingResponse>(
  `offices-response-${props.estateType}`,
  async () => {
    controller?.abort?.()
    // ? -> weird bug https://riesenia.atlassian.net/browse/CBRE-83
    officesResponse.error.value = null

    controller =
      typeof AbortController !== 'undefined'
        ? new AbortController()
        : ({} as AbortController)

    if (props.city) {
      filterState.filterValues.value.city = props.city
    }

    return await useEstateListingData({
      paginationState,
      filterState,
      estateType: props.estateType,
      abortController: controller,
    })
  },
  {
    lazy: true,
    server: false,
    immediate: false,
  }
)

const unwatchResponseWatcher = watchEffect(() => {
  if (officesResponse.pending.value) return

  unwatchResponseWatcher()

  filterState.actions.addRefreshFunction(
    'offices_response',
    officesResponse.refresh
  )
})

watchEffect(() => {
  if (officesResponse.pending.value) return

  paginationState.actions.setResponse(officesResponse)
})

const currentFilterComponent = (() => {
  if (props.estateType === 'office') {
    return OfficeListingFilter
  }

  if (props.estateType === 'retail') {
    return RetailListingFilter
  }

  if (props.estateType === 'warehouse') {
    return WarehouseListingFilter
  }

  return OfficeListingFilter
})()

onMounted(async () => {
  officesResponse.execute()

  if (!window.location.search.includes('scroll-to-hash')) {
    return
  }

  const urlParams = new URLSearchParams(window.location.search)
  const scrollHashEl = document.getElementById(
    urlParams.get('scroll-to-hash') as string
  ) as HTMLElement | null

  urlParams.delete('scroll-to-hash')
  window.history.replaceState(
    {},
    '',
    `${window.location.pathname}?${urlParams.toString()}`
  )

  if (!scrollHashEl) return

  await new Promise((resolve) => setTimeout(resolve, 1))

  scrollToElement({
    element: scrollHashEl,
    ignoreHeaderHeight: true,
    behavior: 'instant',
  })
})

onBeforeUnmount(() => {
  controller?.abort?.()

  clearNuxtData(`offices-response-${props.estateType}`)
})
</script>

<template>
  <div class="c-estate-listing">
    <EstateListingHeader
      class="c-estate-listing__header"
      :estate-type="estateType"
      :offices-response="officesResponse.data.value"
      :offices-pending="officesResponse.pending.value"
      :filter-state="filterState"
    />

    <template v-if="officesResponse.error.value">
      <Error :error="officesResponse.error.value" />
    </template>

    <template v-else>
      <div id="listing-scroll-hook" ref="listingScrollHook" />

      <EstateFilterBase
        class="c-estate-listing__filter"
        :estate-type="estateType"
        :offices-response="officesResponse.data.value"
        :offices-pending="officesResponse.pending.value"
        :filter-state="filterState"
      >
        <component
          :is="currentFilterComponent"
          v-if="officesResponse.data.value"
          :filter-state="filterState"
          :offices-response="officesResponse.data.value"
        />
      </EstateFilterBase>

      <EstateListingSort
        class="c-estate-listing__sort"
        :estate-type="estateType"
        :offices-response="officesResponse.data.value"
        :offices-pending="officesResponse.pending.value"
      />

      <EstateListingMain
        class="c-estate-listing__main"
        :estate-type="estateType"
        :estate-response="officesResponse.data.value"
        :estate-pending="officesResponse.pending.value"
        :pagination-state="paginationState"
      />

      <Container v-if="locale === 'sk'" class="c-estate-listing__article">
        <ShowMore v-if="estateType === 'office' && !currentCity">
          <div class="sc-content">
            <h2>Moderné kancelárske priestory prispôsobené na mieru</h2>

            <p>
              Prenájom kancelárskych priestorov by nemal byť len o výbere
              štyroch či viacerých stien. Je dôležité zameriavať sa na viacero
              faktorov, ako je napríklad
              <strong>lokalita, atmosféra, vybavenie</strong> a podobne. V našej
              ponuke nájdete
              <strong>
                administratívne budovy, zrekonštruované historické priestory
              </strong>
              , ale aj
              <strong>
                kancelárske jednotky v biznis centrách či samostatné objekty.
              </strong>
            </p>

            <p>
              Na výber sú kancelárske priestory na prenájom v centre mesta, ako
              aj vo výhodných lokalitách mimo mestského ruchu
              <strong>s jednoduchým prístupom a možnosťou parkovania</strong>.
              Stačí si len vybrať taký kancelársky priestor, ktorý bude spĺňať
              všetky vaše požiadavky a očakávania.
            </p>

            <p>
              Všetky kancelárie na prenájom, ktoré vám ponúkame,
              <strong>spĺňajú technické aj dizajnové štandardy</strong> , pričom
              zabezpečujú
              <strong>komfort, reprezentatívnosť a dostupnosť</strong>. Stačí si
              len vybrať.
            </p>

            <p>
              Vyskúšajte náš filter kancelárskych priestorov, kde si zvolíte
              mesto, cenu, veľkosť či dostupnosť a následne si môžete vybrať tú
              najlepšiu ponuku, ktorá bude spĺňať všetky vaše požiadavky.
            </p>

            <h2>Na čo sa zamerať pri výbere kancelárie na prenájom?</h2>

            <p>
              Ak si budete vyberať vhodnú kanceláriu na prenájom, mali by ste sa
              pozerať aj na pohodlie zamestnancov aj efektivitu celého tímu.
              Preto by ste mali brať do úvahy tieto kritériá:
            </p>

            <ul>
              <li>
                <strong>Lokalita a dostupnosť</strong> - dôležitá je dobrá
                dopravná dostupnosť (MHD, parkovanie), blízkosť služieb a
                občianskej vybavenosti.
              </li>
              <li>
                <strong>Veľkosť a dispozícia priestoru</strong> - kancelársky
                priestor by mal vyhovovať počtu zamestnancov aj spôsobu práce
                (open space, samostatné kancelárie, zasadačky).
              </li>
              <li>
                <strong>Technické vybavenie</strong> - kvalitná klimatizácia,
                osvetlenie, vysokorýchlostný internet, bezpečnostné systémy a
                podobne.
              </li>
              <li>
                <strong>Flexibilita zmluvy</strong> - možnosť krátkodobého či
                dlhodobého prenájmu kancelárie, možnosť rastu alebo úprav
                priestoru.
              </li>
              <li>
                <strong>Energetická efektivita a udržateľnosť</strong> - moderné
                a nové budovy často ponúkajú úsporné riešenia, ktoré šetria
                náklady. To vás pri kancelárskych priestoroch na prenájom
                rozhodne poteší.
              </li>
              <li>
                <strong>Doplnkové služby</strong> - recepcia, upratovanie,
                spoločné priestory, kantína alebo relax zóny.
              </li>
              <li>
                <strong>Reprezentatívnosť</strong> - vzhľad budovy, v ktorej
                budete mať prenajatú kanceláriu, je tiež veľmi dôležitý. Má to
                vplyv na imidž vašej firmy u klientov aj zamestnancov.
              </li>
            </ul>

            <p>
              Vybrať si ten správny kancelársky priestor je investíciou do
              budúcnosti vášho podnikania. V
              <NuxtLink
                class="c-link c-link--secondary c-link--underlined"
                to="/"
              >
                CBRE Firemné reality</NuxtLink
              >
              vám pomôžeme nájsť miesto, kde sa vaše nápady budú rozvíjať a tím
              rásť. Spoľahnite sa na naše dlhoročné skúsenosti a širokú ponuku
              priestorov prispôsobených vašim potrebám. Spolu vytvoríme základ
              pre váš úspech.
            </p>
          </div>
        </ShowMore>
        <ShowMore v-else-if="estateType === 'office' && currentCity">
          <div class="sc-content">
            <h2>Moderné kancelárske priestory prispôsobené na mieru</h2>

            <p>
              Prenájom kancelárskych priestorov by nemal byť len o výbere
              štyroch či viacerých stien. Je dôležité zameriavať sa na viacero
              faktorov, ako je napríklad
              <strong>lokalita, atmosféra, vybavenie</strong> a podobne. V našej
              ponuke nájdete
              <strong>
                administratívne budovy, zrekonštruované historické priestory
              </strong>
              , ale aj
              <strong>
                kancelárske jednotky v biznis centrách či samostatné objekty.
              </strong>
            </p>

            <p>
              Na výber sú kancelárske priestory na prenájom v centre mesta
              {{ currentCity }}, ako aj vo výhodných lokalitách mimo mestského
              ruchu
              <strong>s jednoduchým prístupom a možnosťou parkovania</strong>.
              Stačí si len vybrať taký kancelársky priestor, ktorý bude spĺňať
              všetky vaše požiadavky a očakávania.
            </p>

            <p>
              Všetky kancelárie na prenájom, ktoré vám ponúkame,
              <strong>spĺňajú technické aj dizajnové štandardy</strong> , pričom
              zabezpečujú
              <strong>komfort, reprezentatívnosť a dostupnosť</strong>. Stačí si
              len vybrať.
            </p>

            <p>
              Vyskúšajte náš filter kancelárskych priestorov v lokalite
              {{ currentCity }}, kde si zvolíte cenu, veľkosť či dostupnosť a
              následne si môžete vybrať tú najlepšiu ponuku, ktorá bude spĺňať
              všetky vaše požiadavky.
            </p>

            <h2>Na čo sa zamerať pri výbere kancelárie na prenájom?</h2>

            <p>
              Ak si budete vyberať vhodnú kanceláriu na prenájom, mali by ste sa
              pozerať aj na pohodlie zamestnancov aj efektivitu celého tímu.
              Preto by ste mali brať do úvahy tieto kritériá:
            </p>

            <ul>
              <li>
                <strong>Lokalita a dostupnosť</strong> - dôležitá je dobrá
                dopravná dostupnosť (MHD, parkovanie), blízkosť služieb a
                občianskej vybavenosti.
              </li>
              <li>
                <strong>Veľkosť a dispozícia priestoru</strong> - kancelársky
                priestor by mal vyhovovať počtu zamestnancov aj spôsobu práce
                (open space, samostatné kancelárie, zasadačky).
              </li>
              <li>
                <strong>Technické vybavenie</strong> - kvalitná klimatizácia,
                osvetlenie, vysokorýchlostný internet, bezpečnostné systémy a
                podobne.
              </li>
              <li>
                <strong>Flexibilita zmluvy</strong> - možnosť krátkodobého či
                dlhodobého prenájmu kancelárie, možnosť rastu alebo úprav
                priestoru.
              </li>
              <li>
                <strong>Energetická efektivita a udržateľnosť</strong> - moderné
                a nové budovy často ponúkajú úsporné riešenia, ktoré šetria
                náklady. To vás pri kancelárskych priestoroch na prenájom
                rozhodne poteší.
              </li>
              <li>
                <strong>Doplnkové služby</strong> - recepcia, upratovanie,
                spoločné priestory, kantína alebo relax zóny.
              </li>
              <li>
                <strong>Reprezentatívnosť</strong> - vzhľad budovy, v ktorej
                budete mať prenajatú kanceláriu, je tiež veľmi dôležitý. Má to
                vplyv na imidž vašej firmy u klientov aj zamestnancov.
              </li>
            </ul>

            <p>
              Vybrať si ten správny kancelársky priestor je investíciou do
              budúcnosti vášho podnikania. V
              <NuxtLink
                class="c-link c-link--secondary c-link--underlined"
                to="/"
              >
                CBRE Firemné reality</NuxtLink
              >
              vám pomôžeme nájsť miesto, kde sa vaše nápady budú rozvíjať a tím
              rásť. Spoľahnite sa na naše dlhoročné skúsenosti a širokú ponuku
              priestorov v meste {{ currentCity }}, prispôsobených vašim
              potrebám. Spolu vytvoríme základ pre váš úspech.
            </p>
          </div>
        </ShowMore>
        <ShowMore v-else-if="estateType === 'warehouse' && !currentCity">
          <div class="sc-content">
            <h2>Sklady a haly na prenájom pre rôzne typy podnikania</h2>

            <p>
              Dopyt po komerčných priestoroch na Slovensku dlhodobo ťahajú dva
              typy nehnuteľností: sklady a výrobné haly. Vhodne zvolený priestor
              ovplyvňuje každodenné fungovanie firmy, náklady na prevádzku aj
              možnosti jej ďalšieho rastu.
            </p>

            <p>
              Pri výbere preto nestačí posudzovať iba výmeru a cenu nájmu.
              Dôležitý je aj účel využitia, technické parametre budovy, dopravná
              dostupnosť, možnosti zásobovania a vybavenie celého areálu.
            </p>

            <p>
              Naša ponuka skladových a výrobných priestorov na prenájom je
              pestrá a nájdete v nej haly
              <strong>
                vhodné na skladovanie, logistiku, distribúciu, výrobu,
                kompletizáciu, servisné činnosti aj ďalšie firemné
                prevádzky</strong
              >.
            </p>

            <p>
              Na jednom mieste tak môžete nájsť priestor pre skladovanie, výrobu
              aj ďalšie časti firemnej prevádzky. Ak potrebujete aj zázemie pre
              administratívu, predaj alebo služby, prezrite si tiež naše
              <NuxtLink to="/kancelarie">kancelárske</NuxtLink> a
              <NuxtLink to="/obchodne-priestory">
                obchodné priestory na prenájom</NuxtLink
              >.
            </p>

            <h2>
              Ako vybrať vhodný skladový alebo výrobný priestor na prenájom?
            </h2>

            <p>
              Pri výbere skladu alebo haly je dôležité vychádzať z konkrétnych
              potrieb vašej prevádzky. Iné požiadavky môže mať firma, ktorá
              hľadá na prenájom menší skladový priestor, a iné spoločnosť, ktorá
              potrebuje rozsiahlu logistickú alebo výrobnú halu.
            </p>

            <p>Najdôležitejšie kritériá sú najmä:</p>

            <ul>
              <li>výmera a svetlá výška priestoru,</li>
              <li>nosnosť podlahy a technický stav objektu,</li>
              <li>prístup pre nákladnú dopravu a napojenie na diaľnicu,</li>
              <li>manipulačné a parkovacie plochy,</li>
              <li>dostupnosť kancelárskeho a sociálneho zázemia,</li>
              <li>možnosti úpravy alebo rozšírenia priestoru.</li>
            </ul>

            <p>
              Pri prenájme skladových priestorov je dôležité posúdiť aj to, ako
              často bude prebiehať príjem a expedícia tovaru a aká manipulačná
              technika sa bude v objekte používať.
            </p>

            <p>
              Pri výbere výrobnej haly treba navyše preveriť kapacitu
              elektrického pripojenia, možnosti odvetrávania, umiestnenie
              technológií a ďalšie prevádzkové požiadavky.
            </p>

            <h2>S odbornou pomocou nájdete vhodný priestor jednoduchšie</h2>

            <p>
              Nájsť vhodný sklad alebo halu na prenájom môže byť náročné, hlavne
              ak musí priestor spĺňať špecifické technické, logistické alebo
              výrobné požiadavky.
            </p>

            <p>
              V <NuxtLink to="/">CBRE Firemné reality</NuxtLink>
              <strong>máme viac ako 440 odborníkov</strong> pôsobiacich v
              regiónoch po celom Slovensku. Vďaka miestnej znalosti trhu vám
              preto
              <strong>
                vieme ukázať existujúce nehnuteľnosti, pripravovanú výstavbu aj
                alternatívne riešenia
              </strong>
              priamo vo vami hľadanom regióne. Porovnáme technické štandardy
              jednotlivých budov a
              <strong>pomôžeme vám vyjednať podmienky nájmu</strong> tak, aby
              zodpovedali vášmu rozpočtu aj plánom na ďalší rast.
            </p>

            <p>
              Okrem skladových priestorov a hál vám radi pomôžeme aj s výberom
              <NuxtLink to="/kancelarie">kancelárskych priestorov</NuxtLink>
              alebo
              <NuxtLink to="/obchodne-priestory">obchodných priestorov</NuxtLink
              >, ak riešite aj tieto časti prevádzky.
            </p>

            <p>
              <NuxtLink to="/kontakt">Kontaktujte nás</NuxtLink> s vašimi
              požiadavkami a
              <strong>my vám pripravíme ponuku na mieru zdarma</strong>.
            </p>
          </div>
        </ShowMore>

        <EstateListingFaq
          v-if="estateType === 'warehouse' && !currentCity"
          class="c-estate-listing__faq"
          title="Často kladené otázky"
          :items="warehouseListingFaq"
        />
      </Container>
    </template>

    <EstateListingServices class="c-estate-listing__services" />

    <TailoredOfferBanner class="c-estate-listing__banner" />
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;
@use '@/assets/styles/mixins/news-content' as contentMixin;

.sc-content {
  @include contentMixin.news-content;

  ul {
    & {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
    }

    @include breakpoint-down('lg') {
      & {
        grid-template-columns: 1fr;
      }
    }
  }
}

#listing-scroll-hook {
  position: relative;
  top: calc(var(--header-height) * -1 - 20px);
  pointer-events: none;
  visibility: hidden;

  @include breakpoint-down('lg') {
    top: calc(var(--header-height) * -1);
  }
}

.c-estate-listing {
  &__header {
    margin-bottom: 40px;

    @include breakpoint-down('lg') {
      margin-bottom: 0;
    }
  }

  &__filter {
    margin-bottom: 56px;

    @include breakpoint-down('lg') {
      margin-bottom: 24px;
    }
  }

  &__sort {
    margin-bottom: 24px;
  }

  &__main {
    margin-bottom: 56px;
  }

  &__services {
    margin-bottom: 72px;
  }

  &__banner {
    margin-bottom: 100px;

    @include breakpoint-down('md') {
      margin-bottom: 40px;
    }
  }

  &__article {
    & {
      padding-bottom: 40px;
    }
  }

  &__faq {
    margin-top: 56px;
  }
}
</style>
